import { Button, Divider, Paper } from '@mui/material';
import { BubbleMenu, type Editor } from '@provision/rich-text-editor';
import { type ComponentProps, Fragment } from 'react';

interface TableBarProps {
  editor: Editor;
}

type TableCommand =
  | 'addRowBefore'
  | 'addRowAfter'
  | 'addColumnBefore'
  | 'addColumnAfter'
  | 'deleteRow'
  | 'deleteColumn'
  | 'deleteTable';

const ACTION_GROUPS: { label: string; command: TableCommand }[][] = [
  [
    { label: 'Row above', command: 'addRowBefore' },
    { label: 'Row below', command: 'addRowAfter' },
    { label: 'Column left', command: 'addColumnBefore' },
    { label: 'Column right', command: 'addColumnAfter' },
  ],
  [
    { label: 'Delete row', command: 'deleteRow' },
    { label: 'Delete column', command: 'deleteColumn' },
    { label: 'Delete table', command: 'deleteTable' },
  ],
];

type BubbleMenuProps = ComponentProps<typeof BubbleMenu>;

// Module constants: BubbleMenu re-registers its plugin whenever these change, and re-registering
// reconfigures the editor, which closes the slash menu mid-keystroke.
const showInTable: BubbleMenuProps['shouldShow'] = ({ editor, state }) =>
  editor.isEditable && editor.isActive('table') && state.selection.empty;

const BAR_OPTIONS: BubbleMenuProps['options'] = { placement: 'bottom' };

export const TableBar = ({ editor }: TableBarProps) => (
  <BubbleMenu
    editor={editor}
    pluginKey="legalDocumentTableBar"
    shouldShow={showInTable}
    options={BAR_OPTIONS}
  >
    <Paper elevation={3} sx={{ display: 'flex', alignItems: 'center', gap: 0.25, p: 0.5 }}>
      {ACTION_GROUPS.map((actions, index) => (
        <Fragment key={actions[0].command}>
          {index > 0 ? <Divider orientation="vertical" flexItem /> : null}
          {actions.map(({ label, command }) => (
            <Button
              key={command}
              size="small"
              color={command.startsWith('delete') ? 'error' : 'inherit'}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => editor.chain().focus()[command]().run()}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', minWidth: 0 }}
            >
              {command.startsWith('add') ? `+ ${label}` : label}
            </Button>
          ))}
        </Fragment>
      ))}
    </Paper>
  </BubbleMenu>
);
